/**
 * Firebase Client Entry
 *
 * Initializes the browser-side Firebase app, Auth, and Firestore (with a
 * persistent IndexedDB local cache so PWA resumes and page reloads don't
 * re-read full listener result sets), and re-exports the provider plus the
 * auth/firestore hooks used throughout the app.
 *
 * Copyright — see the LICENSE file in the repository root for terms.
 */

'use client';

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';

import { firebaseConfig } from './config';
export { firebaseConfig } from './config';

/**
 * Build the client Firestore instance with a persistent (IndexedDB) local
 * cache. Without persistence every page load / PWA resume re-reads the FULL
 * result set of every live listener (heats-for-race, registrations, lap
 * events, pilots…), which exhausted the Firestore read quota on race day —
 * phones at the field lock/unlock constantly. With the persistent cache,
 * listener re-attaches resume from cached state and only deltas are billed.
 * Falls back to the default in-memory cache when IndexedDB is unavailable
 * (private browsing, unsupported browsers) or Firestore was already
 * initialized for this app instance (hot reload / duplicate call).
 *
 * @param app - The initialized Firebase app.
 * @returns The app's Firestore instance.
 */
function initFirestoreWithPersistentCache(app: FirebaseApp): Firestore {
  try {
    return initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
  } catch {
    return getFirestore(app);
  }
}

export function initializeFirebase(): {
  app: FirebaseApp;
  auth: Auth;
  firestore: Firestore;
} {
  const apps = getApps();
  const app = apps.length > 0 ? apps[0] : initializeApp(firebaseConfig);
  const auth = getAuth(app);
  const firestore = initFirestoreWithPersistentCache(app);
  return { app, auth, firestore };
}

export * from './provider';
export * from './auth/use-user';
export * from './firestore/use-collection';
export * from './firestore/use-doc';
